import React from 'react';
import './PrivacyPolicyModal.css';

interface PrivacyPolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const PrivacyPolicyModal: React.FC<PrivacyPolicyModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="privacy-modal-backdrop" onClick={handleBackdropClick}>
      <div className="privacy-modal">
        <div className="privacy-modal-header">
          <h2>Privacy Policy</h2>
          <button 
            className="privacy-modal-close" 
            onClick={onClose}
            aria-label="Close Privacy Policy"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        <div className="privacy-modal-content">
          <div className="privacy-section">
            <p><strong>Effective Date:</strong> October 10, 2025</p>
            <p><strong>Entity:</strong> Intelyx AI</p>
            <p><strong>Website:</strong> <a href="https://www.intelyx.ai" target="_blank" rel="noopener noreferrer">https://www.intelyx.ai</a></p>
          </div>

          <div className="privacy-section">
            <h3>1. Purpose</h3>
            <p>
              This Privacy Policy explains how Intelyx AI ("we", "our", "us") handles limited personal 
              information when you visit our website or communicate with us.
            </p>
            <p>
              We designed our website to operate with <strong>minimal data collection</strong> and <strong>no 
              third-party tracking technologies</strong> such as Google Analytics, LinkedIn Insight Tag, or cookies.
            </p>
          </div>

          <div className="privacy-section">
            <h3>2. What We Collect</h3>
            <p>We only collect the information you <strong>explicitly provide</strong>, for example when you:</p>
            <ul>
              <li>Fill out a "Contact Us" or "Request Demo" form</li>
              <li>Subscribe to a mailing list or send us an email</li>
            </ul>
            
            <p><strong>Information collected may include:</strong></p>
            <ul>
              <li>Name</li>
              <li>Business email address</li>
              <li>Company name</li>
              <li>Message content</li>
            </ul>

            <p>
              We do <strong>not</strong> automatically track or log personal data such as IP address, 
              browser fingerprints, or device identifiers beyond what standard web-server logs record 
              for security (e.g., error diagnostics).
            </p>
          </div>

          <div className="privacy-section">
            <h3>3. How We Use Your Information</h3>
            <p>We use your information solely to:</p>
            <ul>
              <li>Respond to your inquiry or request</li>
              <li>Provide requested product or service information</li>
              <li>Maintain the security and integrity of our website</li>
              <li>Comply with applicable laws and regulations</li>
            </ul>
            <p>
              We do <strong>not</strong> use your data for advertising, behavioral profiling, or sale to third parties.
            </p>
          </div>

          <div className="privacy-section">
            <h3>4. Cookies and Tracking</h3>
            <p>
              Our website currently uses <strong>no cookies</strong>, tracking pixels, or analytics scripts.
            </p>
            <p>
              If we introduce analytics tools in the future (e.g., Google Analytics 4 or LinkedIn Insight Tag), 
              we will update this policy and present a consent banner before activating them.
            </p>
          </div>

          <div className="privacy-section">
            <h3>5. Data Retention</h3>
            <ul>
              <li>Contact form submissions are retained securely for up to <strong>12 months</strong> for follow-up purposes, then deleted automatically.</li>
              <li>System security logs may be retained up to <strong>90 days</strong> for fraud detection and compliance.</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h3>6. Data Security</h3>
            <p>
              Your data is stored on secure AWS services protected 
              with encryption, strict IAM access controls, and network firewalls.
            </p>
            <p>Only authorized personnel with legitimate business needs can access it.</p>
          </div>

          <div className="privacy-section">
            <h3>7. Your Rights</h3>
            <p>Depending on your location, you may have rights to:</p>
            <ul>
              <li>Access, correct, or delete your personal information</li>
              <li>Withdraw consent or object to processing</li>
              <li>Request a copy of your data</li>
            </ul>
            <p>
              To exercise any rights, contact <a href="mailto:privacy@intelyx.ai">privacy@intelyx.ai</a>.
            </p>
          </div>

          <div className="privacy-section">
            <h3>8. Third-Party Links</h3>
            <p>
              Our site may contain links to third-party pages (e.g., LinkedIn, YouTube).
            </p>
            <p>
              We are not responsible for their privacy practices. Please review those sites' privacy policies.
            </p>
          </div>

          <div className="privacy-section">
            <h3>9. Policy Updates</h3>
            <p>
              We may update this Privacy Policy from time to time.
            </p>
          </div>

          <div className="privacy-section">
            <h3>10. Contact</h3>
            <p>Questions about this policy or our data practices:</p>
            <p>📧 <a href="mailto:privacy@intelyx.ai">privacy@intelyx.ai</a></p>
            <p>📍 Intelyx AI, Illinois USA</p>
          </div>
        </div>
      </div>
    </div>
  );
};